import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Theme,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useTranslation } from 'react-i18next';
import { Content } from '../../../common/models/content/Content';
import { useAppDispatch } from '../../../store/hooks';
import {
  ResetContentList,
  UpdateIsContentPreview,
  UpdatePreviewContentId,
} from '../../../store/slices/contentSlice';
import { getContentById, ResponseError } from '../../../store/thunks/contentThunk';
import AuthenticatedRoute from '../../AuthenticatedRoute';
import {
  getHeaderImageUrl,
  getHeading,
  getHeroFormat,
  getImageUrl,
} from './ContentCommonFunctions';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';
import { TestIds } from '../../../assets/testIds';
import { HOME_ROUTE } from '../../../routes/namedRoutes';
import { useSafeBack } from '../../../common/hooks/useSafeBack';
import ContentCard from './ContentCard';

const btnStyle = (theme: Theme) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  margin: `${theme.spacing(1)} ${theme.spacing(0)}`,
  border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
  color: theme.palette.grey[900],
  '&:hover': {
    borderColor: theme.palette.grey[200],
  },
} as const);

export default function ContentPreview() {
  const { id } = useParams();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<Content>();
  const [contentError, setContentError] = useState<ResponseError>();
  const contentViewBaseUrl = '/home/preview';
  const { openExternalLink } = useNavigationUtils();
  const safeBack = useSafeBack(HOME_ROUTE);
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(UpdateIsContentPreview(true));
    dispatch(UpdatePreviewContentId(Number(id)));
    dispatch(getContentById(Number(id)))
      .then(unwrapResult)
      .then((result: Content) => {
        setContent(result);
        dispatch(ResetContentList([result])); // initial content list state
        setLoading(false);
      })
      .catch((error: ResponseError) => {
        setContentError(error);
        setLoading(false);
      });
    return () => {
      dispatch(UpdateIsContentPreview(false)); // cleanup on unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayContentView = (contentItem: Content) => {
    if (contentItem?.attributes?.type?.toUpperCase() !== 'PODCAST') {
      if (contentItem?.attributes?.external_link?.target) {
        openExternalLink(contentItem?.attributes?.external_link.target);
      } else if (contentItem?.attributes?.video_url) {
        openExternalLink(contentItem?.attributes?.video_url);
      } else {
        navigate(`${contentViewBaseUrl}/${id}/detail`);
      }
    }
  };

  return (
    <AuthenticatedRoute>
      <Container maxWidth={false}>
        <Backdrop
          sx={{ color: theme.palette.common.white, zIndex: theme.zIndex.drawer + 1 }}
          open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Button
          data-testid={TestIds.contentArticleView.backBtn}
          startIcon={<ArrowBackIosIcon sx={{ fontSize: `${theme.spacing(2)} !important` }} />}
          sx={[btnStyle,
            {
              backgroundColor: theme.palette.common.white,
              '&:hover': {
                backgroundColor: theme.palette.common.white,
              },
            },
          ]}
          onClick={safeBack}
        >
          {t('button.back')}
        </Button>
        {content && <ContentCard
          key={content.id}
          contentId={content.id}
          contentHeroListener={() => displayContentView(content)}
          contentText={content.attributes?.title}
          contentShortDescription={content.attributes?.short_description}
          categoryText={content.attributes?.type}
          banner={
            {
              url: getImageUrl(
                getHeroFormat(content)?.url
                ,
              ),
              width: getHeroFormat(content)?.width,
              height: getHeroFormat(content)?.height,
            }}
          authorTitle={getHeading(content)}
          publishedDate={
            content.attributes?.source_published_date
          }
          logo={getHeaderImageUrl(content)}
          videoUrl={content.attributes?.video_url}
          contentLikeStatus={content.likeStatus}
          formId={content.attributes?.form_id}
          metadata={content.attributes?.metadata}
          podcast={content.attributes?.podcast}
          contentFeedSection={content.contentFeedSection}
          contentFeedSectionPosition={content.contentFeedSectionPosition}
          locked={content.attributes?.locked}
          lowestMembershipRequired={content.attributes?.lowestMembershipRequired}
          isHorizontalCard={!isMobile}
        />}
        {contentError && <>
          <h1>Error {contentError.statusCode}</h1>
          <h3>{contentError.errorMessage}</h3>
        </>}
      </Container>
    </AuthenticatedRoute >
  );
}
